import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, PencilSimple, Trash, Star, Eye, EyeSlash } from "@phosphor-icons/react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { C } from "@/brand/tokens";
import { resourceCategories6 } from "@/brand/contentF6";
import { F1CMonogram } from "@/components/brand/F1CMonogram";
import { ResourceForm } from "@/components/admin/ResourceForm";
import { usePageMeta } from "@/lib/usePageMeta";
import {
  adminListResources,
  adminCreateResource,
  adminUpdateResource,
  adminDeleteResource,
} from "@/lib/resourcesApi";

const catLabel = Object.fromEntries(resourceCategories6.map((c) => [c.id, c.label]));

/**
 * AdminPage — hidden CMS at /admin (no auth per site-owner choice).
 * Create, edit, feature, publish/unpublish, categorize, and delete
 * Resources library content.
 */
export default function AdminPage() {
  usePageMeta({ title: "Content Studio", description: "Owner-managed Resources CMS.", noindex: true });
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | resource
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => {
    adminListResources()
      .then(setItems)
      .catch((e) => toast.error(e.message));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const save = async (data) => {
    setSaving(true);
    try {
      if (editing && editing !== "new" && editing.id) {
        await adminUpdateResource(editing.id, data);
        toast.success("Resource updated");
      } else {
        await adminCreateResource(data);
        toast.success("Resource created");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (item, key) => {
    try {
      await adminUpdateResource(item.id, { ...item, [key]: !item[key] });
      toast.success(
        key === "featured"
          ? item.featured ? "Removed from featured" : "Marked as featured"
          : item.published ? "Unpublished" : "Published"
      );
      refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await adminDeleteResource(item.id);
      toast.success("Resource deleted");
      refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <main
      data-testid="admin-page"
      className="min-h-screen"
      style={{ background: C.surfaceLight, color: C.textOnLight, fontFamily: "Inter, sans-serif" }}
    >
      <Toaster position="top-right" richColors />

      <header
        className="sticky top-0 z-30"
        style={{
          background: C.bgDark,
          borderBottom: `1px solid ${C.brassSoft}`,
        }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 h-20 flex items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <F1CMonogram size={40} tone="onDark" />
            <div>
              <p
                className="font-cormorant leading-none"
                style={{ color: C.textOnDark, fontSize: "1.2rem" }}
              >
                Content Studio
              </p>
              <p
                className="font-inter text-[9.5px] uppercase font-semibold mt-1"
                style={{ color: C.accentMetal, letterSpacing: "0.24em" }}
              >
                Resources CMS
              </p>
            </div>
          </div>
          <Link
            to="/resources"
            data-testid="admin-view-site"
            className="font-inter text-[10.5px] uppercase font-semibold border-b pb-0.5"
            style={{
              color: C.accentMetal,
              borderColor: C.accentMetal,
              letterSpacing: "0.2em",
            }}
          >
            View public library
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 py-12">
        <div className="flex flex-wrap items-center justify-between gap-5 mb-8">
          <div>
            <h1
              className="font-cormorant"
              style={{ color: C.textOnLight, fontSize: "2rem", lineHeight: 1.1 }}
            >
              Resources library
            </h1>
            <p
              className="mt-2 font-inter"
              style={{ color: C.inkOnLight, fontSize: "0.92rem" }}
            >
              {items.length} item{items.length === 1 ? "" : "s"} ·{" "}
              {items.filter((i) => i.published).length} published ·{" "}
              {items.filter((i) => i.featured).length} featured
            </p>
          </div>
          {!editing && (
            <button
              type="button"
              data-testid="admin-new-resource"
              onClick={() => setEditing("new")}
              className="inline-flex items-center gap-2.5 px-6 py-3.5 font-inter text-[11px] uppercase font-semibold"
              style={{
                background: C.surfacePrimary,
                color: C.textOnDark,
                letterSpacing: "0.2em",
              }}
            >
              <Plus size={13} weight="bold" />
              New resource
            </button>
          )}
        </div>

        {editing && (
          <div className="mb-10">
            <ResourceForm
              key={editing === "new" ? "new" : editing.id}
              initial={editing === "new" ? null : editing}
              onSave={save}
              onCancel={() => setEditing(null)}
              saving={saving}
            />
          </div>
        )}

        <div className="space-y-3" data-testid="admin-resource-list">
          {items.map((item, i) => (
            <article
              key={item.id}
              data-testid={`admin-resource-${i}`}
              className="p-6 flex flex-wrap items-center gap-x-6 gap-y-4"
              style={{
                background: item.published ? C.surfaceLight2 : "#EFEAE0",
                border: `1px solid ${C.brassLine}`,
                opacity: item.published ? 1 : 0.75,
              }}
            >
              <div className="min-w-0 grow basis-64">
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className="font-inter text-[9.5px] uppercase font-bold px-2 py-0.5"
                    style={{
                      color: C.bgDark,
                      background: C.accentMetal,
                      letterSpacing: "0.14em",
                    }}
                  >
                    {catLabel[item.category] || item.category}
                  </span>
                  {item.featured && (
                    <span
                      className="inline-flex items-center gap-1 font-inter text-[9.5px] uppercase font-semibold"
                      style={{ color: C.accentMetal, letterSpacing: "0.14em" }}
                    >
                      <Star size={10} weight="fill" /> Featured
                    </span>
                  )}
                  {!item.published && (
                    <span
                      className="font-inter text-[9.5px] uppercase font-semibold"
                      style={{ color: C.accentFunctional, letterSpacing: "0.14em" }}
                    >
                      Draft / unpublished
                    </span>
                  )}
                </div>
                <p
                  className="mt-2 font-cormorant truncate"
                  style={{ color: C.textOnLight, fontSize: "1.25rem", lineHeight: 1.2 }}
                >
                  {item.title}
                </p>
                <p
                  className="mt-1 font-inter text-[11px]"
                  style={{ color: C.inkOnLightSoft }}
                >
                  {item.author} · {item.publish_date} · /resources/{item.slug}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  data-testid={`admin-toggle-featured-${i}`}
                  title={item.featured ? "Remove from featured" : "Mark as featured"}
                  onClick={() => toggle(item, "featured")}
                  className="inline-flex items-center justify-center w-10 h-10"
                  style={{
                    border: `1px solid ${C.brassLine}`,
                    color: item.featured ? C.accentMetal : C.inkOnLightSoft,
                    background: "transparent",
                  }}
                >
                  <Star size={15} weight={item.featured ? "fill" : "regular"} />
                </button>
                <button
                  type="button"
                  data-testid={`admin-toggle-published-${i}`}
                  title={item.published ? "Unpublish" : "Publish"}
                  onClick={() => toggle(item, "published")}
                  className="inline-flex items-center justify-center w-10 h-10"
                  style={{
                    border: `1px solid ${C.brassLine}`,
                    color: item.published ? C.accentFunctional : C.inkOnLightSoft,
                    background: "transparent",
                  }}
                >
                  {item.published ? <Eye size={15} /> : <EyeSlash size={15} />}
                </button>
                <button
                  type="button"
                  data-testid={`admin-edit-${i}`}
                  title="Edit"
                  onClick={() => {
                    setEditing(item);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="inline-flex items-center justify-center w-10 h-10"
                  style={{
                    border: `1px solid ${C.brassLine}`,
                    color: C.textOnLight,
                    background: "transparent",
                  }}
                >
                  <PencilSimple size={15} />
                </button>
                <button
                  type="button"
                  data-testid={`admin-delete-${i}`}
                  title="Delete"
                  onClick={() => remove(item)}
                  className="inline-flex items-center justify-center w-10 h-10"
                  style={{
                    border: `1px solid ${C.brassLine}`,
                    color: "#8C3B2E",
                    background: "transparent",
                  }}
                >
                  <Trash size={15} />
                </button>
              </div>
            </article>
          ))}
          {items.length === 0 && (
            <p
              className="font-cormorant italic py-12"
              style={{ color: C.inkOnLightSoft, fontSize: "1.2rem" }}
            >
              No resources yet — create the first one.
            </p>
          )}
        </div>
      </div>
    </main>
  );
}
